import { useEffect, useState } from 'react'

/**
 * SSE state used for polling decisions. This module provides:
 *
 * 1. Force-disconnect (testing): treat SSE as disconnected → use aggressive polling.
 * 2. SSE connected for polling: shared state so right rail and list use relaxed polling when SSE is connected.
 *
 * Force-disconnect triggers:
 * - Query param: ?sseDisconnect=true
 * - LocalStorage: key `realtime_sse_force_disconnect` = 'true'
 *
 * When force-disconnect is true, the app should not connect to SSE and should use aggressive polling.
 * Toggling localStorage and dispatching the custom event updates consumers without a full reload.
 */

const STORAGE_KEY = 'realtime_sse_force_disconnect'
const CUSTOM_EVENT = 'realtime_sse_force_disconnect_change'

function readFromUrl(): boolean {
  if (typeof window === 'undefined') return false
  const params = new URLSearchParams(window.location.search)
  return params.get('sseDisconnect') === 'true'
}

function readFromStorage(): boolean {
  if (typeof window === 'undefined') return false
  try {
    return window.localStorage?.getItem(STORAGE_KEY) === 'true'
  } catch {
    return false
  }
}

/**
 * Returns true if SSE should be forced disconnected (for testing fallback behavior).
 * Checks query param first, then localStorage.
 */
export function getForceSSEDisconnect(): boolean {
  return readFromUrl() || readFromStorage()
}

/**
 * Set or clear the force-disconnect flag in localStorage. Dispatches a custom event
 * so useForceSSEDisconnect() subscribers re-render. Use for testing (e.g. a "Simulate disconnect" control).
 * Query param overrides localStorage; to clear when ?sseDisconnect=true, remove the param and reload.
 */
export function setForceSSEDisconnect(force: boolean): void {
  if (typeof window === 'undefined') return
  try {
    if (force) {
      window.localStorage?.setItem(STORAGE_KEY, 'true')
    } else {
      window.localStorage?.removeItem(STORAGE_KEY)
    }
    window.dispatchEvent(new CustomEvent(CUSTOM_EVENT))
  } catch {
    // ignore
  }
}

/**
 * Reactive hook for force-disconnect. Subscribes to storage and custom events so
 * toggling via setForceSSEDisconnect() updates the UI without reload.
 */
export function useForceSSEDisconnect(): boolean {
  const [force, setForce] = useState(getForceSSEDisconnect)

  useEffect(() => {
    const onUpdate = () => setForce(getForceSSEDisconnect())
    window.addEventListener(CUSTOM_EVENT, onUpdate)
    window.addEventListener('storage', onUpdate)
    return () => {
      window.removeEventListener(CUSTOM_EVENT, onUpdate)
      window.removeEventListener('storage', onUpdate)
    }
  }, [])

  return force
}

// ---------------------------------------------------------------------------
// SSE connected for polling (shared state for right rail and other consumers)
// ---------------------------------------------------------------------------

const SSE_CONNECTED_FOR_POLLING_KEY = 'realtime_sse_connected_for_polling'
const SSE_CONNECTED_FOR_POLLING_EVENT = 'realtime_sse_connected_for_polling_change'

function readSSEConnectedForPollingFromStorage(): boolean {
  if (typeof window === 'undefined') return false
  try {
    return window.localStorage?.getItem(SSE_CONNECTED_FOR_POLLING_KEY) === 'true'
  } catch {
    return false
  }
}

/**
 * Returns true if SSE is considered connected for polling (relaxed cadence).
 * Reads from localStorage; defaults to false when unset or in SSR.
 */
export function getSSEConnectedForPolling(): boolean {
  return readSSEConnectedForPollingFromStorage()
}

/**
 * Set or clear the SSE-connected-for-polling flag. Dispatches a custom event
 * so useSSEConnectedForPolling() subscribers re-render. Call from code that
 * knows the real SSE connection state (e.g. useLiveMeetingsPolling).
 */
export function setSSEConnectedForPolling(connected: boolean | undefined): void {
  if (typeof window === 'undefined') return
  try {
    if (connected === true) {
      window.localStorage?.setItem(SSE_CONNECTED_FOR_POLLING_KEY, 'true')
    } else {
      window.localStorage?.removeItem(SSE_CONNECTED_FOR_POLLING_KEY)
    }
    window.dispatchEvent(new CustomEvent(SSE_CONNECTED_FOR_POLLING_EVENT))
  } catch {
    // ignore
  }
}

/**
 * Reactive hook for SSE-connected-for-polling. Subscribes to storage and custom
 * events so when a writer calls setSSEConnectedForPolling(), consumers (e.g.
 * right rail polling) update without reload.
 */
export function useSSEConnectedForPolling(): boolean {
  const [connected, setConnected] = useState(getSSEConnectedForPolling)

  useEffect(() => {
    const onUpdate = () => setConnected(getSSEConnectedForPolling())
    window.addEventListener(SSE_CONNECTED_FOR_POLLING_EVENT, onUpdate)
    window.addEventListener('storage', onUpdate)
    return () => {
      window.removeEventListener(SSE_CONNECTED_FOR_POLLING_EVENT, onUpdate)
      window.removeEventListener('storage', onUpdate)
    }
  }, [])

  return connected
}
